import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import * as api from '../lib/api';
import { COVERS } from '../lib/media';
import { VIDEOS } from '../lib/media';
import HeroVideo from '../components/HeroVideo';

export default function Programs() {
  const [booksList, setBooksList] = useState<api.BookListItem[]>([]);

  useEffect(() => {
    api.books.list().then(setBooksList).catch(() => {});
  }, []);

  // Find books by slug/title
  const carnivoreBook = booksList.find(b => b.slug === 'fasting-carnivore' || b.title?.toLowerCase().includes('fasting carnivore') && !b.title?.toLowerCase().includes('kitchen') && !b.title?.toLowerCase().includes('performance'));
  const fastingBook = booksList.find(b => b.slug === 'nano-fasting' || b.title?.toLowerCase().includes('nano'));
  const performanceBook = booksList.find(b => b.slug === 'fasting-carnivore-performance' || b.title?.toLowerCase().includes('performance'));
  const kitchenBook = booksList.find(b => b.slug === 'fasting-carnivore-kitchen' || b.title?.toLowerCase().includes('kitchen'));

  return (
    <>
      {/* Hero */}
      <section className="hero">
        <HeroVideo className="video-bg" src={VIDEOS.meatHero} />
        <div className="video-overlay" />
        <div className="container programs-hero-container">
          <motion.div
            className="hero-content text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="hero-label">Complete Library</span>
            <h1>Our Programs</h1>
            <p>Four comprehensive books. One complete system. Everything you need to learn, apply, and thrive.</p>
            <Link to="/join" className="btn btn-primary btn-lg">
              Get All Four Books — $97
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="section">
        <div className="container">
          <div className="content-narrow">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <h2 className="heading-section">Everything You Need to Succeed</h2>
              <p className="programs-intro-text">
                Whether you're just starting out or looking to optimize your approach, our library has you covered.
                Each book tackles a different aspect of the carnivore and fasting lifestyle, giving you the complete
                toolkit for understanding, implementation, and long-term success. No gaps. No confusion. Just clarity and action.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Book 1: Fasting Carnivore */}
      <section className="section section-light">
        <div className="container">
          <div className="two-col programs-book-grid">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <img
                src={carnivoreBook?.coverUrl || COVERS.carnivore}
                alt="Fasting Carnivore Book"
                className="programs-book-cover"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="section-label">Book One</span>
              <h2 className="programs-book-title">Fasting Carnivore</h2>
              <p className="programs-book-subtitle">
                Stories of Transformation
              </p>
              <p className="programs-book-desc">
                Twelve powerful stories of people who transformed their lives through carnivore eating.
                Follow their journeys from struggle to breakthrough. Learn from their mistakes. Apply their lessons to your own path.
              </p>
              <ul className="programs-book-features">
                <BookFeature text="12 transformation stories" />
                <BookFeature text="The science behind carnivore" />
                <BookFeature text="Getting started guide" />
                <BookFeature text="Troubleshooting common issues" />
              </ul>
              <Link to="/carnivore" className="btn btn-primary">
                Learn More
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Book 2: Nano Fasting */}
      <section className="section">
        <div className="container">
          <div className="two-col programs-book-grid">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="programs-order-last"
            >
              <span className="section-label">Book Two</span>
              <h2 className="programs-book-title">Nano Fasting</h2>
              <p className="programs-book-subtitle">
                The Effortless Approach to Extended Fasting
              </p>
              <p className="programs-book-desc">
                Forget willpower. Forget suffering. Learn how to extend your fasting window week after
                week without it ever feeling hard. This isn't about pushing through—it's about making fasting feel natural and effortless.
              </p>
              <ul className="programs-book-features">
                <BookFeature text="30-day progressive protocol" />
                <BookFeature text="Understanding hunger signals" />
                <BookFeature text="Breaking fasts properly" />
                <BookFeature text="Troubleshooting stalls" />
              </ul>
              <Link to="/fasting" className="btn btn-primary">
                Learn More
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-center programs-order-first"
            >
              <img
                src={fastingBook?.coverUrl || COVERS.nanoFasting}
                alt="Nano Fasting Book"
                className="programs-book-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Book 3: Performance */}
      <section className="section section-light">
        <div className="container">
          <div className="two-col programs-book-grid">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <img
                src={performanceBook?.coverUrl || COVERS.performance}
                alt="Fasting Carnivore Performance Book"
                className="programs-book-cover"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="section-label">Book Three</span>
              <h2 className="programs-book-title">Fasting Carnivore Performance</h2>
              <p className="programs-book-subtitle">
                Unlock Your Athletic Potential
              </p>
              <p className="programs-book-desc">
                Train harder. Recover faster. Build muscle while staying lean. Everything you need
                to optimize athletic performance on a meat-based diet—without carbs, without bonking, without the energy roller coaster.
              </p>
              <ul className="programs-book-features">
                <BookFeature text="Fat-adapted training protocols" />
                <BookFeature text="Pre and post-workout nutrition" />
                <BookFeature text="Body composition strategies" />
                <BookFeature text="Recovery optimization" />
              </ul>
              <Link to="/performance" className="btn btn-primary">
                Learn More
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Book 4: Kitchen */}
      <section className="section">
        <div className="container">
          <div className="two-col programs-book-grid">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="programs-order-last"
            >
              <span className="section-label">Book Four</span>
              <h2 className="programs-book-title">Fasting Carnivore Kitchen</h2>
              <p className="programs-book-subtitle">
                Simple, Delicious Meat-Based Recipes
              </p>
              <p className="programs-book-desc">
                Carnivore doesn't have to be boring. Master the art of cooking meat with complete
                recipes, cooking techniques, and meal prep strategies. This is how you make meat-based eating something you actually look forward to every day.
              </p>
              <ul className="programs-book-features">
                <BookFeature text="Complete recipe collection" />
                <BookFeature text="Meal prep guides" />
                <BookFeature text="Shopping lists" />
                <BookFeature text="Cooking times and temperatures" />
              </ul>
              <Link to="/kitchen" className="btn btn-primary">
                Learn More
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-center programs-order-first"
            >
              <img
                src={kitchenBook?.coverUrl || COVERS.kitchen}
                alt="Fasting Carnivore Kitchen Book"
                className="programs-book-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="section section-light">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="programs-included-header"
          >
            <h2 className="heading-section">Everything Included</h2>
            <p className="programs-text-light">One payment. Lifetime access. No subscriptions. No hidden fees. Just everything you need.</p>
          </motion.div>

          <div className="grid-2 programs-included-grid">
            <IncludedItem text="All 4 complete books" />
            <IncludedItem text="Workbooks and guides" />
            <IncludedItem text="Private Discord community" />
            <IncludedItem text="Future updates included" />
            <IncludedItem text="24/7 community support" />
            <IncludedItem text="Lifetime access" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta">
        <div className="container text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>Get the Complete Library</h2>
            <p>
              All four books. All workbooks. Lifetime community access. Everything you need to learn, apply, and thrive—together.
            </p>
            <Link to="/join" className="btn btn-primary btn-lg">
              Join Now — $97 Lifetime
            </Link>
            <p className="programs-cta-note">
              One-time payment. No subscriptions. Instant access. Start learning in minutes.
            </p>
          </motion.div>
        </div>
      </section>
    </>
  );
}

function BookFeature({ text }: { text: string }) {
  return (
    <li className="programs-feature-item">
      <Check size={18} color="var(--accent)" />
      {text}
    </li>
  );
}

function IncludedItem({ text }: { text: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="programs-included-item"
    >
      <Check size={20} color="var(--accent)" />
      <span className="programs-included-text">{text}</span>
    </motion.div>
  );
}
